import React, { useEffect, useMemo, useRef, useState } from 'react';

// Global "jump to any screen" command palette (Ctrl-K / "/").
// Fed a flat, permission-filtered list of {moduleKey, moduleLabel, screenKey, screenLabel}
// from App.js; navigation + recent tracking are handled by the parent via onSelect.
export default function CommandPalette({ open, items, recent, onClose, onSelect, isMobile = false }) {
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (open) { setQ(''); setSel(0); const t = setTimeout(() => inputRef.current?.focus(), 20); return () => clearTimeout(t); }
  }, [open]);

  const results = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) {
      const byKey = new Map(items.map((i) => [i.screenKey, i]));
      return (recent || []).map((k) => byKey.get(k)).filter(Boolean).slice(0, 8);
    }
    const starts = [], contains = [];
    items.forEach((i) => {
      const name = i.screenLabel.toLowerCase();
      if (name.startsWith(query)) starts.push(i);
      else if (name.includes(query) || i.moduleLabel.toLowerCase().includes(query)) contains.push(i);
    });
    return [...starts, ...contains].slice(0, 50);
  }, [q, items, recent]);

  useEffect(() => { setSel(0); }, [q]);
  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector('[data-sel="true"]');
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [sel, open]);

  if (!open) return null;

  const choose = (i) => { if (i) onSelect(i); };
  const onKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSel((s) => Math.min(s + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); choose(results[sel]); }
    else if (e.key === 'Escape') { e.preventDefault(); onClose(); }
  };
  const hl = (text) => {
    const query = q.trim();
    if (!query) return text;
    const i = text.toLowerCase().indexOf(query.toLowerCase());
    if (i < 0) return text;
    return (<>{text.slice(0, i)}<mark style={{ background: 'transparent', color: 'var(--theme-primary, #0d8f83)', fontWeight: 800 }}>{text.slice(i, i + query.length)}</mark>{text.slice(i + query.length)}</>);
  };

  const overlay = { position: 'fixed', inset: 0, background: 'rgba(8,18,30,0.45)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: isMobile ? '8vh 10px 0' : '12vh 12px 0', zIndex: 4000 };
  const box = { width: isMobile ? '100%' : 'min(560px, 94vw)', maxWidth: '560px', maxHeight: isMobile ? '82vh' : 'none', display: 'flex', flexDirection: 'column', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', boxShadow: '0 24px 60px rgba(8,18,30,0.35)', overflow: 'hidden' };
  const input = { width: '100%', boxSizing: 'border-box', border: 'none', borderBottom: '1px solid #e2e8f0', outline: 'none', padding: isMobile ? '14px 15px' : '16px 18px', fontSize: '16px', color: '#0f1f33' };
  const list = { flex: 1, maxHeight: isMobile ? '60vh' : '340px', overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '6px' };
  const groupLabel = { fontSize: '10px', fontWeight: 800, letterSpacing: '0.11em', textTransform: 'uppercase', color: '#94a3b8', padding: '8px 12px 3px' };
  const row = { display: 'flex', alignItems: 'center', gap: '10px', padding: isMobile ? '12px' : '9px 12px', borderRadius: '9px', fontSize: '0.85rem', cursor: 'pointer', color: '#0f1f33' };
  const rowSel = { background: 'var(--theme-primary-soft, #d6f1ed)' };
  const empty = { padding: '22px', textAlign: 'center', color: '#94a3b8', fontSize: '0.85rem' };
  const foot = { display: 'flex', gap: '14px', padding: '9px 14px', borderTop: '1px solid #e2e8f0', color: '#94a3b8', fontSize: '11px' };

  return (
    <div onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }} style={overlay} role="dialog" aria-modal="true" aria-label="Command search">
      <div style={box}>
        <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey}
          placeholder={isMobile ? 'Jump to any screen…' : 'Jump to any screen…  (try “invoice”, “inventory”, “error log”)'} autoComplete="off" style={input} />
        <div ref={listRef} style={list}>
          {results.length === 0 ? (
            <div style={empty}>No screens match {q.trim() ? `“${q.trim()}”` : ''}.</div>
          ) : (
            <>
              <div style={groupLabel}>{q.trim() ? 'Screens' : 'Recently used'}</div>
              {results.map((i, idx) => (
                <div key={i.screenKey} data-sel={idx === sel} onMouseMove={() => setSel(idx)} onClick={() => choose(i)}
                  style={{ ...row, ...(idx === sel ? rowSel : {}) }}>
                  <span style={{ opacity: 0.5 }}>⌕</span>
                  <span><b style={{ fontWeight: 600 }}>{hl(i.screenLabel)}</b> <span style={{ color: '#94a3b8', fontSize: '0.74rem' }}>— {i.moduleLabel}</span></span>
                </div>
              ))}
            </>
          )}
        </div>
        {!isMobile && <div style={foot}><span>↑ ↓ navigate</span><span>↵ open</span><span>esc close</span></div>}
      </div>
    </div>
  );
}
